import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr, storePublicBase } from "@/lib/api";
import { useMerchantAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, GraduationCap, Plus, Trash2 } from "lucide-react";

const PRESETS = ["Best Seller", "Highest Rated"];
const ICONS = [
  ["check", "Check"],
  ["star", "Star"],
  ["play", "Play"],
  ["book", "Book"],
  ["clock", "Clock"],
  ["award", "Award"],
  ["users", "People"],
  ["globe", "Globe"],
];

const blank = () => ({
  title: "",
  slug: "",
  short_description: "",
  description: "",
  tags: [],
  video_url: "",
  author_name: "",
  language: "English",
  rating_avg: 4.8,
  rating_count: 0,
  learn_title: "What you'll learn",
  learn_items: [{ icon: "check", text: "" }],
  modules: [{ title: "", length_label: "", lectures: [{ title: "", duration: "" }] }],
  price: "",
  taglines: ["Full lifetime access"],
});

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

export default function AdminCoursePages() {
  const { tenant } = useMerchantAuth();
  const [tab, setTab] = useState("pages");
  const [items, setItems] = useState([]);
  const [max, setMax] = useState(10);
  const [leads, setLeads] = useState([]);
  const [draft, setDraft] = useState(null);
  const [tagDraft, setTagDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(false);

  const loadPages = () => {
    api.get("/merchant/course-pages").then(({ data }) => {
      setItems(data.items || []);
      setMax(data.max || 10);
      setLocked(false);
    }).catch((e) => {
      if (String(extractErr(e)).includes("not active")) setLocked(true);
      else toast.error(extractErr(e));
    });
  };
  const loadLeads = () => {
    api.get("/merchant/course-pages/leads").then(({ data }) => setLeads(data.items || [])).catch((e) => toast.error(extractErr(e)));
  };
  useEffect(() => { loadPages(); }, []);
  useEffect(() => { if (tab === "leads") loadLeads(); }, [tab]);

  const pageUrl = (slug) => `${storePublicBase(tenant)}/course/${slug}`;

  const copyUrl = async (slug) => {
    try {
      await navigator.clipboard.writeText(pageUrl(slug));
      toast.success("Page link copied");
    } catch {
      toast.error("Could not copy the link");
    }
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    const payload = {
      ...draft,
      price: draft.price === "" ? 0 : Number(draft.price),
      rating_avg: Number(draft.rating_avg || 0),
      rating_count: Number(draft.rating_count || 0),
    };
    try {
      if (draft.id) await api.put(`/merchant/course-pages/${draft.id}`, payload);
      else await api.post("/merchant/course-pages", payload);
      toast.success(draft.id ? "Course page saved" : "Course page created");
      setDraft(null);
      loadPages();
    } catch (e) { toast.error(extractErr(e)); }
    finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete “${row.title}”? The public link will stop working.`)) return;
    try {
      await api.delete(`/merchant/course-pages/${row.id}`);
      toast.success("Course page deleted");
      if (draft?.id === row.id) setDraft(null);
      loadPages();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const removeLead = async (row) => {
    try {
      await api.delete(`/merchant/course-pages/leads/${row.id}`);
      setLeads((rows) => rows.filter((r) => r.id !== row.id));
    } catch (e) { toast.error(extractErr(e)); }
  };

  const patch = (partial) => setDraft((d) => ({ ...d, ...partial }));
  const addTag = (label) => {
    const next = String(label || "").trim();
    if (!next || (draft.tags || []).some((t) => t.toLowerCase() === next.toLowerCase())) return;
    if ((draft.tags || []).length >= 6) return;
    patch({ tags: [...(draft.tags || []), next] });
    setTagDraft("");
  };

  if (locked) {
    return (
      <div className="max-w-xl" data-testid="admin-course-pages">
        <h1 className="font-display text-2xl font-bold">Course Page</h1>
        <p className="text-sm text-slate-500 mt-2">Turn on Course Page Maker in the App Store to publish course pages.</p>
        <Link to="/admin/addons" className="inline-block mt-3 text-sm underline">Open App Store</Link>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-5xl" data-testid="admin-course-pages">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 text-violet-700 text-xs font-semibold uppercase tracking-wide"><GraduationCap className="w-4 h-4" /> Course Page Maker</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1">Course Page</h1>
          <p className="text-sm text-slate-500 mt-1">Up to {max} course pages. Each one gets a public link and its own enquiry form.</p>
        </div>
      </div>

      <div className="inline-flex rounded-xl bg-slate-100 p-1">
        <button type="button" data-testid="course-tab-pages" onClick={() => setTab("pages")} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "pages" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Pages</button>
        <button type="button" data-testid="course-tab-leads" onClick={() => setTab("leads")} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "leads" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Contact Form Data</button>
      </div>

      {tab === "pages" && (
        <div className="space-y-4">
          {!draft && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-slate-500">{items.length} of {max} pages</p>
              <Button type="button" data-testid="course-page-new" disabled={items.length >= max} onClick={() => setDraft(blank())}>
                <Plus className="w-4 h-4 mr-1" /> New course page
              </Button>
            </div>
          )}

          {draft && (
            <div className="rounded-2xl border bg-white p-4 sm:p-6 space-y-4" data-testid="course-page-editor">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Course title">
                  <Input data-testid="course-title" value={draft.title} onChange={(e) => patch({ title: e.target.value })} />
                </Field>
                <Field label="Page address (optional)">
                  <Input value={draft.slug} placeholder="auto from title" onChange={(e) => patch({ slug: e.target.value })} />
                </Field>
              </div>
              <Field label="Short description">
                <textarea className="w-full min-h-[72px] rounded-md border px-3 py-2 text-sm" value={draft.short_description} onChange={(e) => patch({ short_description: e.target.value })} />
              </Field>
              <Field label="Tags">
                <div className="flex flex-wrap gap-2">
                  {PRESETS.map((tag) => (
                    <button key={tag} type="button" className={`rounded-full px-3 py-1 text-xs font-semibold border ${(draft.tags || []).includes(tag) ? "bg-violet-700 text-white border-violet-700" : "bg-white text-slate-600"}`} onClick={() => ((draft.tags || []).includes(tag) ? patch({ tags: draft.tags.filter((t) => t !== tag) }) : addTag(tag))}>{tag}</button>
                  ))}
                  {(draft.tags || []).filter((t) => !PRESETS.includes(t)).map((tag) => (
                    <button key={tag} type="button" className="rounded-full px-3 py-1 text-xs font-semibold bg-slate-900 text-white" onClick={() => patch({ tags: draft.tags.filter((t) => t !== tag) })}>{tag} ×</button>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input value={tagDraft} placeholder="Custom tag" onChange={(e) => setTagDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(tagDraft); } }} />
                  <Button type="button" variant="outline" onClick={() => addTag(tagDraft)}>Add</Button>
                </div>
              </Field>
              <Field label="Intro video link (YouTube, Vimeo, or MP4). No upload.">
                <Input data-testid="course-video" value={draft.video_url} placeholder="https://www.youtube.com/watch?v=…" onChange={(e) => patch({ video_url: e.target.value })} />
              </Field>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="Created by">
                  <Input value={draft.author_name} placeholder="Author name" onChange={(e) => patch({ author_name: e.target.value })} />
                </Field>
                <Field label="Course language">
                  <Input value={draft.language} onChange={(e) => patch({ language: e.target.value })} />
                </Field>
                <Field label="Rating stars (0–5)">
                  <Input type="number" min="0" max="5" step="0.1" value={draft.rating_avg} onChange={(e) => patch({ rating_avg: e.target.value })} />
                </Field>
                <Field label="Rated by (customers)">
                  <Input type="number" min="0" value={draft.rating_count} onChange={(e) => patch({ rating_count: e.target.value })} />
                </Field>
              </div>
              <Field label="What you'll learn — section title">
                <Input value={draft.learn_title} onChange={(e) => patch({ learn_title: e.target.value })} />
              </Field>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-500">Learn points (icon + text)</Label>
                {(draft.learn_items || []).map((row, i) => (
                  <div key={i} className="flex flex-col sm:flex-row gap-2">
                    <select className="h-9 rounded-md border px-2 text-sm sm:w-32" value={row.icon} onChange={(e) => {
                      const learn_items = draft.learn_items.slice();
                      learn_items[i] = { ...row, icon: e.target.value };
                      patch({ learn_items });
                    }}>
                      {ICONS.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                    </select>
                    <Input value={row.text} placeholder="Point" onChange={(e) => {
                      const learn_items = draft.learn_items.slice();
                      learn_items[i] = { ...row, text: e.target.value };
                      patch({ learn_items });
                    }} />
                    <Button type="button" variant="outline" onClick={() => patch({ learn_items: draft.learn_items.filter((_, n) => n !== i) })}>Remove</Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => patch({ learn_items: [...(draft.learn_items || []), { icon: "check", text: "" }] })}>Add point</Button>
              </div>
              <div className="space-y-3">
                <Label className="text-xs font-semibold text-slate-500">Course modules</Label>
                {(draft.modules || []).map((mod, mi) => (
                  <div key={mi} className="rounded-xl border p-3 space-y-2">
                    <div className="grid sm:grid-cols-[1fr_140px_auto] gap-2">
                      <Input value={mod.title} placeholder="Module title" onChange={(e) => {
                        const modules = draft.modules.slice();
                        modules[mi] = { ...mod, title: e.target.value };
                        patch({ modules });
                      }} />
                      <Input value={mod.length_label} placeholder="Length, e.g. 1hr 4min" onChange={(e) => {
                        const modules = draft.modules.slice();
                        modules[mi] = { ...mod, length_label: e.target.value };
                        patch({ modules });
                      }} />
                      <Button type="button" variant="outline" onClick={() => patch({ modules: draft.modules.filter((_, n) => n !== mi) })}>Remove</Button>
                    </div>
                    {(mod.lectures || []).map((lec, li) => (
                      <div key={li} className="grid sm:grid-cols-[1fr_120px_auto] gap-2 pl-0 sm:pl-4">
                        <Input value={lec.title} placeholder="Lecture" onChange={(e) => {
                          const modules = draft.modules.slice();
                          const lectures = (mod.lectures || []).slice();
                          lectures[li] = { ...lec, title: e.target.value };
                          modules[mi] = { ...mod, lectures };
                          patch({ modules });
                        }} />
                        <Input value={lec.duration} placeholder="5min" onChange={(e) => {
                          const modules = draft.modules.slice();
                          const lectures = (mod.lectures || []).slice();
                          lectures[li] = { ...lec, duration: e.target.value };
                          modules[mi] = { ...mod, lectures };
                          patch({ modules });
                        }} />
                        <Button type="button" variant="ghost" onClick={() => {
                          const modules = draft.modules.slice();
                          modules[mi] = { ...mod, lectures: mod.lectures.filter((_, n) => n !== li) };
                          patch({ modules });
                        }}>Remove</Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => {
                      const modules = draft.modules.slice();
                      modules[mi] = { ...mod, lectures: [...(mod.lectures || []), { title: "", duration: "" }] };
                      patch({ modules });
                    }}>Add lecture</Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => patch({ modules: [...(draft.modules || []), { title: "", length_label: "", lectures: [{ title: "", duration: "" }] }] })}>Add module</Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Course price (₹)">
                  <Input type="number" min="0" data-testid="course-price" value={draft.price} onChange={(e) => patch({ price: e.target.value })} />
                </Field>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-500">Taglines under the price</Label>
                {(draft.taglines || []).map((line, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={line} placeholder="Full lifetime access" onChange={(e) => {
                      const taglines = draft.taglines.slice();
                      taglines[i] = e.target.value;
                      patch({ taglines });
                    }} />
                    <Button type="button" variant="outline" onClick={() => patch({ taglines: draft.taglines.filter((_, n) => n !== i) })}>Remove</Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => patch({ taglines: [...(draft.taglines || []), ""] })}>Add tagline</Button>
              </div>
              <Field label="Description">
                <textarea className="w-full min-h-[140px] rounded-md border px-3 py-2 text-sm" value={draft.description} onChange={(e) => patch({ description: e.target.value })} />
              </Field>
              <div className="flex flex-wrap justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
                <Button type="button" data-testid="course-page-save" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save page"}</Button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {items.map((row) => (
              <div key={row.id} className="rounded-xl border bg-white p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`course-row-${row.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{row.title}</div>
                  <div className="text-xs text-slate-500 truncate">{pageUrl(row.slug)}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setDraft({ ...blank(), ...row, price: row.price ?? "" })}>Edit</Button>
                  <Button type="button" variant="outline" size="sm" data-testid={`course-copy-${row.id}`} onClick={() => copyUrl(row.slug)}><Copy className="w-3.5 h-3.5 mr-1" /> Copy URL</Button>
                  <Button type="button" variant="outline" size="sm" data-testid={`course-delete-${row.id}`} onClick={() => remove(row)}><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
                </div>
              </div>
            ))}
            {items.length === 0 && !draft && <div className="rounded-xl border border-dashed p-8 text-center text-sm text-slate-500">No course pages yet.</div>}
          </div>
        </div>
      )}

      {tab === "leads" && (
        <div className="overflow-x-auto rounded-2xl border bg-white" data-testid="course-leads">
          <table className="min-w-[720px] w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-3">Date</th>
                <th className="px-3 py-3">Course</th>
                <th className="px-3 py-3">Full name</th>
                <th className="px-3 py-3">Phone</th>
                <th className="px-3 py-3">Email</th>
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {leads.map((row) => (
                <tr key={row.id} className="border-t">
                  <td className="px-3 py-3 whitespace-nowrap">{String(row.created_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-3">{row.course_title || "—"}</td>
                  <td className="px-3 py-3">{row.name}</td>
                  <td className="px-3 py-3">{row.phone}</td>
                  <td className="px-3 py-3">{row.email}</td>
                  <td className="px-3 py-3 text-right"><button type="button" className="text-xs text-slate-500 underline" onClick={() => removeLead(row)}>Delete</button></td>
                </tr>
              ))}
              {leads.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No enquiries yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
